import { useState } from 'react';
import useSWR from 'swr';
import axios from 'axios';
import { toast } from 'sonner';
import { useDebounce } from 'use-debounce';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { useGivesAsks } from '@/hooks/use-gives-asks';
import {
  giveAskApi,
  type GiveAskDirectoryItem,
  type GiveAskFilters,
  type GiveAskType,
} from '@/lib/give-ask-api';
import { GiveAskCard } from './gives-asks-card';
import { SendLeadSheet } from './send-lead-sheet';
import { FulfilAskDialog, FulfilFeedbackDialog } from './fulfil-dialogs';
import { AskRepliesSheet } from './ask-replies-sheet';

function errorMessage(error: unknown, fallback: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
  }
  return fallback;
}

const TYPE_TABS: { value: GiveAskType | null; label: string }[] = [
  { value: null, label: 'All' },
  { value: 'GIVE', label: 'Gives' },
  { value: 'ASK', label: 'Asks' },
];

export function GivesAsksView({ communityId }: { communityId: string }) {
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 400);
  const [type, setType] = useState<GiveAskType | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [mine, setMine] = useState(false);
  const [showFulfilled, setShowFulfilled] = useState(false);

  const filters: GiveAskFilters = {
    search: debouncedSearch.trim(),
    type,
    status: showFulfilled ? 'ALL' : 'OPEN',
    category,
    saved,
    mine,
    page: 1,
  };

  const { items, currentUserId, hasMore, isLoading, isLoadingMore, error, loadMore, updateItem, reload } =
    useGivesAsks(communityId, filters);

  const { data: facets } = useSWR(['gives-asks-facets', communityId], () => giveAskApi.facets(communityId));

  const [leadTarget, setLeadTarget] = useState<GiveAskDirectoryItem | null>(null);
  const [fulfilTarget, setFulfilTarget] = useState<GiveAskDirectoryItem | null>(null);
  const [feedbackTarget, setFeedbackTarget] = useState<GiveAskDirectoryItem | null>(null);
  const [repliesTarget, setRepliesTarget] = useState<GiveAskDirectoryItem | null>(null);

  const handleBookmark = async (item: GiveAskDirectoryItem) => {
    updateItem(item.id, { bookmarked: !item.bookmarked });
    try {
      const result = await giveAskApi.toggleBookmark(item.id);
      updateItem(item.id, { bookmarked: result.bookmarked });
    } catch (err) {
      updateItem(item.id, { bookmarked: item.bookmarked });
      toast.error(errorMessage(err, 'Could not update saved items'));
    }
  };

  const confirmFulfil = async () => {
    const target = fulfilTarget;
    if (!target) return;
    try {
      await giveAskApi.fulfil(target.id);
      updateItem(target.id, { status: 'FULFILLED', fulfilledAt: new Date().toISOString() });
      setFulfilTarget(null);
      setFeedbackTarget(target);
      toast.success('Marked as fulfilled');
    } catch (err) {
      toast.error(errorMessage(err, 'Could not mark as fulfilled'));
    }
  };

  const submitFeedback = async (payload: Parameters<typeof giveAskApi.submitFeedback>[1]) => {
    const target = feedbackTarget;
    if (!target) return;
    try {
      await giveAskApi.submitFeedback(target.id, payload);
      toast.success('Thanks for the feedback');
      setFeedbackTarget(null);
    } catch (err) {
      toast.error(errorMessage(err, 'Could not save feedback'));
    }
  };

  const topCategories = facets?.categories.slice(0, 8) ?? [];

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold text-[var(--navy)]">Gives & Asks</h1>
        <p className="text-sm text-muted-foreground">What members offer and what they are looking for.</p>
      </header>

      <div className="space-y-4 rounded-2xl border bg-white p-4 shadow-sm">
        <Input
          type="search"
          placeholder="Search titles, descriptions, businesses or people"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="flex flex-wrap items-center gap-2">
          {TYPE_TABS.map((tab) => (
            <Button
              key={tab.label}
              size="sm"
              variant={type === tab.value ? 'default' : 'outline'}
              onClick={() => setType(tab.value)}
            >
              {tab.label}
              {facets && tab.value ? ` (${facets.types[tab.value]})` : ''}
            </Button>
          ))}
          <Button size="sm" variant={saved ? 'default' : 'outline'} onClick={() => setSaved((v) => !v)}>
            Saved
          </Button>
          <Button size="sm" variant={mine ? 'default' : 'outline'} onClick={() => setMine((v) => !v)}>
            Mine
          </Button>
          <label className="ml-auto flex items-center gap-2 text-sm text-muted-foreground">
            <Switch checked={showFulfilled} onCheckedChange={setShowFulfilled} />
            Show fulfilled
          </label>
        </div>

        {topCategories.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {topCategories.map((entry) => (
              <button
                key={entry.category}
                type="button"
                onClick={() => setCategory(category === entry.category ? null : entry.category)}
                className={`rounded-full border px-3 py-1 text-xs ${
                  category === entry.category ? 'border-teal-600 bg-teal-50 text-teal-800' : 'bg-white text-muted-foreground'
                }`}
              >
                {entry.category} ({entry.count})
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {error ? <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p> : null}

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-40 rounded-2xl" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed bg-white p-10 text-center text-sm text-muted-foreground">
          Nothing matches these filters yet.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {items.map((item) => (
            <GiveAskCard
              key={item.id}
              item={item}
              isOwn={item.owner.id === currentUserId}
              onBookmark={() => handleBookmark(item)}
              onReply={() => setLeadTarget(item)}
              onFulfil={() => setFulfilTarget(item)}
              onReplies={() => setRepliesTarget(item)}
            />
          ))}
        </div>
      )}

      {hasMore ? (
        <div className="flex justify-center">
          <Button variant="outline" onClick={loadMore} disabled={isLoadingMore}>
            {isLoadingMore ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Load more
          </Button>
        </div>
      ) : null}

      <SendLeadSheet
        communityId={communityId}
        target={leadTarget}
        owner={
          leadTarget
            ? {
                name: [leadTarget.owner.firstName, leadTarget.owner.lastName].filter(Boolean).join(' ') || 'Member',
                image: leadTarget.owner.image,
              }
            : null
        }
        onOpenChange={(open) => {
          if (!open) setLeadTarget(null);
        }}
        onSent={(giveAskId) => {
          updateItem(giveAskId, { hasReplied: true });
          void reload();
        }}
      />

      <FulfilAskDialog
        open={fulfilTarget !== null}
        onOpenChange={(open) => {
          if (!open) setFulfilTarget(null);
        }}
        onConfirm={confirmFulfil}
      />

      <FulfilFeedbackDialog
        open={feedbackTarget !== null}
        onOpenChange={(open) => {
          if (!open) setFeedbackTarget(null);
        }}
        onSubmit={submitFeedback}
        onSkip={() => setFeedbackTarget(null)}
      />

      <AskRepliesSheet
        target={repliesTarget}
        onOpenChange={(open) => {
          if (!open) setRepliesTarget(null);
        }}
      />
    </div>
  );
}
